import Dexie from 'dexie';

const DB_NAME = 'FigshelfMessagesDB';
const DB_VERSION = 1;

const normalizeIso = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

export const makeConversationKey = (userId, conversationId) => `${userId || 'anon'}:${conversationId}`;

class MessagesCacheDb extends Dexie {
  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION).stores({
      conversations: 'conversationKey, userId, conversationId, lastMessageAt, updatedAt',
      messages: '[conversationKey+messageId], conversationKey, messageId, createdAt',
    });

    this.conversations = this.table('conversations');
    this.messages = this.table('messages');
  }
}

let dbInstance = null;

const isDatabaseClosedError = (error) => {
  if (!error) return false;
  if (error?.name === 'DatabaseClosedError') return true;
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('database has been closed')) {
    return true;
  }
  if (typeof error?.message === 'string' && error.message.toLowerCase().includes('db.open() was cancelled')) {
    return true;
  }
  return false;
};

const resetMessagesCacheDb = () => {
  // Do not call db.close() here.
  // Dexie can throw/reject during close/open cancellation races (e.g. after indexedDB.deleteDatabase()),
  // which can surface as unhandled rejections. Dropping the singleton reference is enough; a fresh
  // instance will be created on the next read/write.
  dbInstance = null;
};

export const getMessagesCacheDb = () => {
  if (dbInstance && typeof dbInstance.isOpen === 'function' && !dbInstance.isOpen()) {
    resetMessagesCacheDb();
  }

  if (!dbInstance) {
    dbInstance = new MessagesCacheDb();
  }
  return dbInstance;
};

export const cacheConversations = async (userId, conversations) => {
  if (!userId || !Array.isArray(conversations) || conversations.length === 0) return;

  const records = conversations
    .filter((c) => c && c.id)
    .map((c) => {
      const conversationKey = makeConversationKey(userId, c.id);
      const lastMessageAt = normalizeIso(c.lastMessageAt || c.lastMessage?.createdAt || c.updatedAt);
      const updatedAt = normalizeIso(c.updatedAt || c.lastMessageAt || c.lastMessage?.createdAt);

      return {
        conversationKey,
        userId,
        conversationId: c.id,
        lastMessageAt,
        updatedAt,
        data: c,
      };
    });

  if (records.length === 0) return;

  const run = async () => {
    const db = getMessagesCacheDb();
    await db.conversations.bulkPut(records);
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetMessagesCacheDb();
      await run();
      return;
    }
    throw error;
  }
};

export const getCachedConversations = async (userId, max = 100) => {
  if (!userId) return [];

  let rows;
  try {
    const db = getMessagesCacheDb();
    rows = await db.conversations.where('userId').equals(userId).toArray();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetMessagesCacheDb();
      const db = getMessagesCacheDb();
      rows = await db.conversations.where('userId').equals(userId).toArray();
    } else {
      throw error;
    }
  }

  const sorted = rows
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.lastMessageAt || a.updatedAt || 0).getTime();
      const bTime = new Date(b.lastMessageAt || b.updatedAt || 0).getTime();
      return bTime - aTime;
    })
    .slice(0, max);

  return sorted.map((r) => r.data).filter(Boolean);
};

export const cacheConversation = async (userId, conversation) => {
  if (!userId || !conversation?.id) return;
  await cacheConversations(userId, [conversation]);
};

export const getCachedConversation = async (userId, conversationId) => {
  if (!userId || !conversationId) return null;

  const conversationKey = makeConversationKey(userId, conversationId);
  try {
    const db = getMessagesCacheDb();
    const row = await db.conversations.get(conversationKey);
    return row?.data || null;
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetMessagesCacheDb();
      const db = getMessagesCacheDb();
      const row = await db.conversations.get(conversationKey);
      return row?.data || null;
    }
    throw error;
  }
};

export const cacheMessages = async (userId, conversationId, messages, maxPerConversation = 500) => {
  if (!userId || !conversationId || !Array.isArray(messages) || messages.length === 0) return;

  const conversationKey = makeConversationKey(userId, conversationId);

  const records = messages
    .filter((m) => m && (m.id || m.messageId))
    .map((m) => {
      const messageId = m.id || m.messageId;
      return {
        conversationKey,
        messageId,
        createdAt: normalizeIso(m.createdAt) || new Date().toISOString(),
        data: m,
      };
    });

  if (records.length === 0) return;

  const run = async () => {
    const db = getMessagesCacheDb();
    await db.transaction('rw', db.messages, async () => {
      await db.messages.bulkPut(records);

      if (maxPerConversation && maxPerConversation > 0) {
        const count = await db.messages.where('conversationKey').equals(conversationKey).count();
        if (count > maxPerConversation) {
          const all = await db.messages.where('conversationKey').equals(conversationKey).toArray();
          all.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          const toDelete = all.slice(0, count - maxPerConversation);
          if (toDelete.length > 0) {
            await db.messages.bulkDelete(toDelete.map((r) => [r.conversationKey, r.messageId]));
          }
        }
      }
    });
  };

  try {
    await run();
  } catch (error) {
    if (isDatabaseClosedError(error)) {
      resetMessagesCacheDb();
      await run();
      return;
    }
    throw error;
  }
};

export const upsertMessage = async (userId, conversationId, message) => {
  if (!userId || !conversationId || !message) return;

  const messageId = message.id || message.messageId;
  if (!messageId) return;

  await cacheMessages(userId, conversationId, [message]);
};

export const getCachedMessages = async (userId, conversationId, max = 200) => {
  if (!userId || !conversationId) return [];

  const db = getMessagesCacheDb();
  const conversationKey = makeConversationKey(userId, conversationId);

  const rows = await db.messages.where('conversationKey').equals(conversationKey).toArray();

  const sorted = rows
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return sorted.slice(Math.max(0, sorted.length - max)).map((r) => r.data).filter(Boolean);
};

export const removeCachedConversation = async (userId, conversationId) => {
  if (!userId || !conversationId) return;

  const db = getMessagesCacheDb();
  const conversationKey = makeConversationKey(userId, conversationId);

  await db.transaction('rw', db.conversations, db.messages, async () => {
    await db.conversations.delete(conversationKey);
    const msgRows = await db.messages.where('conversationKey').equals(conversationKey).toArray();
    if (msgRows.length > 0) {
      await db.messages.bulkDelete(msgRows.map((r) => [r.conversationKey, r.messageId]));
    }
  });
};
